/**
 * ListFilters — single-row filter bar above the table.
 *
 * Surfaces: search (debounced), autonomy multi-select, date-range,
 * "show archived". Kept minimal — Notion-style: collapse → inline.
 */
import { Search, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import type { AutonomyLevel, CardListFilters } from './list-types';

const AUTONOMY_OPTIONS: ReadonlyArray<{ value: AutonomyLevel; label: string }> = [
  { value: 'HITL', label: 'HITL' },
  { value: 'HOTL', label: 'HOTL' },
  { value: 'HOOL', label: 'HOOL' },
  { value: 'agent_only', label: 'Agent only' },
];

export interface ListFiltersProps {
  value: CardListFilters;
  onChange: (next: CardListFilters) => void;
}

export function ListFilters({ value, onChange }: ListFiltersProps) {
  const [searchInput, setSearchInput] = React.useState(value.search ?? '');

  // Debounce 250ms so typing doesn't re-filter on every keystroke.
  React.useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== (value.search ?? '')) {
        onChange({ ...value, search: searchInput || undefined });
      }
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, value.search, value, onChange]);

  const activeFilters = [
    value.search ? `Search "${value.search}"` : null,
    value.autonomy?.length ? `Autonomy ${value.autonomy.length}` : null,
    value.due_after ? `Due after ${value.due_after.slice(0, 10)}` : null,
    value.due_before ? `Due before ${value.due_before.slice(0, 10)}` : null,
  ].filter((s): s is string => Boolean(s));

  return (
    <div className="flex items-center gap-2 border-b border-border bg-background px-3 py-2">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
          size={12}
          aria-hidden
        />
        <input
          type="search"
          value={searchInput}
          placeholder="Search…"
          onChange={(e) => setSearchInput(e.target.value)}
          className={cn(
            'h-7 w-48 rounded border border-border bg-background pl-7 pr-2 text-xs text-foreground',
            'placeholder:text-muted-foreground focus:border-foreground/30 focus:outline-none',
          )}
        />
      </div>

      <AutonomyDropdown
        selected={value.autonomy ?? []}
        onChange={(next) => onChange({ ...value, autonomy: next.length ? next : undefined })}
      />

      <DateRangeInput
        after={value.due_after}
        before={value.due_before}
        onChange={(after, before) => onChange({ ...value, due_after: after, due_before: before })}
      />

      <div className="ml-auto flex flex-wrap items-center gap-1">
        {activeFilters.map((f) => (
          <span
            key={f}
            className="rounded border border-border bg-foreground/[0.04] px-1.5 py-0.5 text-2xs text-muted-foreground"
          >
            {f}
          </span>
        ))}
        {activeFilters.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              onChange({});
            }}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-2xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
          >
            <X size={10} aria-hidden /> clear
          </button>
        )}
      </div>
    </div>
  );
}

function AutonomyDropdown({
  selected,
  onChange,
}: {
  selected: AutonomyLevel[];
  onChange: (next: AutonomyLevel[]) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function toggle(value: AutonomyLevel) {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'h-7 rounded border border-border bg-background px-2 text-xs text-foreground',
          'transition-colors hover:bg-foreground/[0.04]',
          selected.length > 0 && 'border-foreground/30',
        )}
      >
        Autonomy {selected.length > 0 && `(${selected.length})`}
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-44 rounded border border-border bg-background py-1 shadow-md">
          {AUTONOMY_OPTIONS.map((o) => (
            <label
              key={o.value}
              className="flex cursor-pointer items-center gap-2 px-2 py-1 text-xs hover:bg-foreground/[0.04]"
            >
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="h-3.5 w-3.5 accent-foreground"
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DateRangeInput({
  after,
  before,
  onChange,
}: {
  after?: string | undefined;
  before?: string | undefined;
  onChange: (after: string | undefined, before: string | undefined) => void;
}) {
  return (
    <div className="flex items-center gap-1 text-2xs text-muted-foreground">
      <span>Due</span>
      <input
        type="date"
        value={after ? after.slice(0, 10) : ''}
        onChange={(e) => onChange(e.target.value || undefined, before)}
        className="h-7 rounded border border-border bg-background px-1 text-xs text-foreground"
      />
      <span>→</span>
      <input
        type="date"
        value={before ? before.slice(0, 10) : ''}
        onChange={(e) => onChange(after, e.target.value || undefined)}
        className="h-7 rounded border border-border bg-background px-1 text-xs text-foreground"
      />
    </div>
  );
}
